<template>
    <div class="importer-action">
      <modal :id="importer.id" ref="import" @hide="resetState">
        <template slot="title">{{ importer.name }}</template>
        <template slot="body">
          <p>{{ importer.description }}</p>
  
          <template v-if="step === 'settings'">
            <div v-for="(setting, name) in importer.settings" :key="name" class="field">
              <label :class="{ required: setting.required }">{{ setting.label }}</label>
  
              <div class="field" v-if="setting.type === 'file'">
                
                <input
                  type="file"
                  :name="name"
                  :accept="setting['content-accept'] || '*/*'"
                  :required="setting.required"
                  @change="handleFileUpload($event, name)"
                />
              </div>
  
              <div class="input-group" v-if="['text', 'email', 'url', 'number'].includes(setting.type)">
                <input
                  :type="setting.type"
                  class="form-control"
                  v-model="settings_values[name]"
                  :required="setting.required"
                />
              </div>

              <div class="input-group" v-if="['keyvalue'].includes(setting.type)">
                    <keyvalue v-model="settings_values[name]" :variant="setting.variant"></keyvalue>
                </div>

                <div class="input-group" v-if="['textarea'].includes(setting.type)">
                    <textarea class="form-control" v-model="settings_values[name]" :required="setting.required"></textarea>
                </div>

                <div class="input-group" v-if="['int'].includes(setting.type)">
                    <div class="input-group-prepend" v-if="setting.prefix">
                        <span class="btn btn-dark">{{ setting.prefix }}</span>
                    </div>
                    <input class="form-control" v-model="settings_values[name]" type="number" step="1" pattern="\d*"
                        :required="setting.required" />
                    <div class="input-group-append" v-if="setting.suffix">
                        <span class="btn btn-dark">{{ setting.suffix }}</span>
                    </div>
                </div>

                <div class="input-group" v-if="['money'].includes(setting.type)">
                    <div class="input-group-prepend">
                        <span class="btn btn-dark">{{
                            setting.prefix || t('currency_symbol')
                        }}</span>
                    </div>
                    <input class="form-control" name="fixed_amount" v-model="settings_values[name]" key="fixed-amount"
                        type="text" v-money="money" :required="setting.required" />
                    <div class="input-group-append" v-if="setting.suffix">
                        <span class="btn btn-dark">{{ setting.suffix }}</span>
                    </div>
                </div>

                <div class="input-group" v-if="['select'].includes(setting.type)">
                    <select class="form-control" v-model="settings_values[name]" :required="setting.required">
                        <option v-for="option in setting.options" :key="option.value"
                            :selected="option.value == setting.default" :value="option.value">{{ option.label }}
                        </option>
                    </select>
                </div>
  
                <span v-if="errors[name]" class="text-danger">{{ errors[name] }}</span>

            </div>
          </template>
  
          <template v-if="step === 'loading'">
            <div class="text-center p-5">
              <span class="far loader fa-spinner fa-spin fa-3x"></span>
              <p class="mt-3">{{ t('clients-importer-sending_data') }}</p>
            </div>
          </template>
  
          <template v-if="step === 'queued'">
              <div class="text-center p-4">
                  <span class="far fa-check-circle fa-3x text-success"></span>
                  <p class="mt-3">{{ t('clients-importer-queued_message') }}</p>
                  <router-link to="/imports" @click.native="closeModal">
                      {{ t('clients-importer-queued_link') }}
                  </router-link>
              </div>
          </template>

          <template v-if="step === 'success'">
              <div class="alert alert-success">
                  {{ t('clients-importer-import_success') }}
              </div>
              <table class="table" v-if="result && Object.keys(result).length > 0">
                  <tr v-for="(value, key) in result" :key="key">
                      <td>{{ key }}</td>
                      <td>{{ value }}</td>
                  </tr>
              </table>
          </template>
  
          <template v-if="step === 'error'">
               <div class="alert alert-danger">
                 {{ errorMessage }}
              </div>
          </template>
  
        </template>
        <template slot="footer">
          <!-- Footer for Settings Step -->
          <template v-if="step === 'settings'">
            <button class="btn btn-secondary" @click="closeModal">
              {{ t('clients-importer-cancel') }}
            </button>
            <button class="btn btn-success" :disabled="!isFormValid" @click="runImport">
              {{ t('clients-importer-import') }}
            </button>
          </template>
  
          <!-- Footer for Queued/Success/Error Step -->
          <template v-if="['queued', 'success', 'error'].includes(step)">
            <button class="btn btn-primary" @click="closeModal">
              {{ t('clients-importer-close') }}
            </button>
          </template>
        </template>
      </modal>

    </div>
  </template>
  
  <script>
  import keviApi from "../keviApi.js"; // Assuming keviApi is your API wrapper
  
  export default {
    props: {
      importer: {
        type: Object,
        required: false,
       
      }
    },
    data() {
      return {
        step: "settings",
        settings_values: {},
        result: {},
        errorMessage: "",
        errors: {},
        importHistoryId: null,
      };
    },
    computed: {
      /**
       * Validates the form.
       * Checks if all fields marked as 'required' in the settings have a value.
       */
       isFormValid() {
        if (this.step !== 'settings') return true;

        for (const key in this.importer.settings) {
          const setting = this.importer.settings[key];
          if (setting.required && !this.settings_values[key]) return false;
          if (this.errors[key]) return false;
        }
        return true;
      },

    },
    methods: {
      t(term, replaces) {
        return this.$root.t(term, replaces);
      },
      /**
       * Initializes the settings_values object with null for each setting.
       * This ensures reactivity for all form fields.
       */
       initializeForm() {
        this.settings_values = {};
        this.errors = {};
        if (this.importer && this.importer.settings) {
          for (const key in this.importer.settings) {
            this.$set(this.settings_values, key, null);
            this.$set(this.errors, key, null);
          }
        }
      },

      /**
       * Opens the modal and resets its state.
       */
      openModal() {
        this.step = "settings";
        this.result = {};
        this.errorMessage = "";
        this.importHistoryId = null;
        this.initializeForm();
        this.$refs.import.open();
      },
  
      /**
       * Closes the modal.
       */
      closeModal() {
        this.$refs.import.close();
      },

      /**
       * Resets the component state when the modal is hidden.
       */
      resetState() {
        this.step = "settings";
        this.result = {};
        this.errorMessage = "";
        this.importHistoryId = null;
        this.initializeForm();
      },
  
      /**
       * Handles file uploads from input fields.
       * @param {Event} event - The file input change event.
       * @param {string} name - The name of the setting to associate the file with.
       */
       handleFileUpload(event, name) {
        const file = event.target.files[0] || null;
        const setting = this.importer.settings[name];
        const maxKb = setting?.maxFileSizeInKb;

        if (file && maxKb && file.size / 1024 > maxKb) {
          this.errors[name] = this.t('clients-importer-file_size_error', [maxKb]);
          event.target.value = '';
          this.$set(this.settings_values, name, null);
          return;
        }

        this.errors[name] = null; // limpa erro
        this.$set(this.settings_values, name, file);
      },
  
      /**
       * Executes the import process.
       * It constructs a FormData object and sends it to the backend.
       */
      runImport() {
        if (!this.isFormValid) {
          return;
        }
        this.step = "loading";
  
        // Use FormData to correctly handle file uploads
        const formData = new FormData();
        for (const key in this.settings_values) {
          const value = this.settings_values[key];
          if (value) {
              formData.append(key, value);
          }
        }
  
        // Construct the API endpoint URL dynamically using the importer's ID
        const endpoint = `clients/importer/${this.importer.plugins_places_id}/run/${this.importer.id}`;
        const options = keviApi.getOptions(endpoint, "POST", formData, true);
        options.timeout = 6000000; // timeout em milissegundos (10 segundos)
   
        this.$http(options).then(response => {
          // The importer now runs in the background; 202 means the job was queued.
          if (response.status === 202) {
            this.importHistoryId = response.body.import_history_id;
            this.step = "queued";
            this.$emit("queued", this.importHistoryId);
          } else {
            this.result = response.body;
            this.step = "success";
          }
        }).catch(error => {
          this.errorMessage = error.body && error.body.message
            ? error.body.message
            : this.t('clients-importer-import_error');
          this.step = "error";
        });
      },
    },
    watch: {
      // Re-initialize the form if the importer prop changes
      importer: {
        handler: 'initializeForm',
        immediate: true
      }
    },
  };
  </script>
  
  <style scoped lang="scss">
  .field {
    margin-top: 20px;
    margin-bottom: 15px;
  
    label {
      font-size: 14px;
      font-weight: 500;
      display: block;
      margin-bottom: 5px;
    }
  
    .required {
      font-weight: 600;
      &:after {
        content: " *";
        color: #dc3545; /* Bootstrap's danger color */
      }
    }
  }
  
  .loader {
      color: #007bff; /* Bootstrap's primary color */
  }
  
  ::v-deep {
    .modal-dialog {
      max-width: 800px;
    }
  }
  </style>
  